import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import * as api from '@/features/backlog/api';
import { Member } from '@/features/backlog/types';
import { setAccessToken, setActorId, setOnUnauthorized } from '@/utils/http';
import { oidcConfig, oidcEnabled } from './oidc-config';

/**
 * Identity has two modes, chosen by runtime config:
 *  - OIDC (window.config.oidcIssuer set): Zitadel SSO. oidc-client-ts owns the tokens; we
 *    push the ID token into the http layer as a Bearer and resolve the backend Member
 *    (auto-provisioned from the verified token) for `me`.
 *  - Legacy (no issuer): the original password-free email sign-in — you name yourself with an
 *    email that resolves to (or creates) a member, sent as X-Actor-Id. Kept for offline dev.
 * Both expose the same context so the rest of the app is mode-agnostic.
 */
interface IdentityCtx {
  me: Member | null;
  /** True when running under Zitadel SSO (vs the legacy email flow). */
  oidc: boolean;
  /** Auth/session is resolving — render a spinner, not the sign-in screen. */
  loading: boolean;
  /** Legacy email sign-in (throws in OIDC mode; the SSO screen never calls it). */
  signIn: (email: string, name?: string) => Promise<Member>;
  /** Start the Zitadel redirect login (OIDC mode). */
  loginWithSSO: () => void;
  signOut: () => void;
  /** Re-fetch the current member (e.g. after an admin changes your role). */
  reloadMe: () => Promise<void>;
}

const ME_KEY = 'spark-board.me';
/** Where to return after an SSO round-trip. Set (in sessionStorage, which survives the redirect to
 *  Zitadel and back in the same tab) just before signinRedirect, consumed by the /auth/callback
 *  route. Lets a shared deep link — e.g. /spark-product-backlog/SPB-289?item=… — be restored after
 *  login instead of always landing on the boards index. */
export const RETURN_TO_KEY = 'spark-board.postLoginRedirect';
const captureReturnTo = () => {
  try {
    const p = window.location.pathname + window.location.search + window.location.hash;
    if (p && p !== '/' && !p.startsWith('/auth/callback')) sessionStorage.setItem(RETURN_TO_KEY, p);
  } catch {
    /* ignore unavailable storage */
  }
};

const Ctx = createContext<IdentityCtx | null>(null);

export const useIdentity = (): IdentityCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useIdentity must be used within IdentityProvider');
  return v;
};

const loadMe = (): Member | null => {
  try {
    const s = localStorage.getItem(ME_KEY);
    return s ? (JSON.parse(s) as Member) : null;
  } catch {
    return null;
  }
};
const persistMe = (m: Member | null) => {
  try {
    if (m) localStorage.setItem(ME_KEY, JSON.stringify(m));
    else localStorage.removeItem(ME_KEY);
  } catch {
    /* ignore storage failures (private mode) */
  }
};

/** Top-level provider: mounts the OIDC bridge (under AuthProvider) or the legacy provider. */
export const IdentityProvider = ({ children }: { children: ReactNode }) => {
  if (oidcEnabled) {
    return (
      <AuthProvider {...oidcConfig}>
        <OidcIdentityProvider>{children}</OidcIdentityProvider>
      </AuthProvider>
    );
  }
  return <LegacyIdentityProvider>{children}</LegacyIdentityProvider>;
};

/** OIDC bridge — runs inside AuthProvider so it can consume useAuth(). */
function OidcIdentityProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [me, setMe] = useState<Member | null>(null);
  const [resolving, setResolving] = useState(false);

  // On a 401 the token is expired/invalid — drop it and restart the login redirect.
  useEffect(() => {
    setOnUnauthorized(() => {
      setAccessToken(null);
      captureReturnTo();
      void auth.signinRedirect();
    });
    return () => setOnUnauthorized(null);
  }, [auth]);

  // Keep the http Bearer in sync with the current ID token (login + silent renewal).
  useEffect(() => {
    setAccessToken(auth.user?.id_token ?? null);
  }, [auth.user?.id_token]);

  // Once authenticated, resolve (and auto-provision) the backend member for `me`.
  useEffect(() => {
    let cancelled = false;
    if (auth.isAuthenticated && auth.user) {
      setAccessToken(auth.user.id_token ?? null); // ensure it's set before the call flushes
      setResolving(true);
      api
        .resolveSession()
        .then((m) => {
          if (cancelled) return;
          setMe(m);
          persistMe(m);
        })
        .catch(() => {
          /* leave me null; a 401 (if the token is bad) will trigger re-auth */
        })
        .finally(() => {
          if (!cancelled) setResolving(false);
        });
    } else {
      setMe(null);
      persistMe(null);
    }
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.user]);

  const signIn = useCallback(async (): Promise<Member> => {
    throw new Error('email sign-in is disabled when SSO is enabled');
  }, []);
  const loginWithSSO = useCallback(() => {
    captureReturnTo();
    void auth.signinRedirect();
  }, [auth]);
  const signOut = useCallback(() => {
    setMe(null);
    persistMe(null);
    setAccessToken(null);
    void auth.signoutRedirect();
  }, [auth]);
  const reloadMe = useCallback(async () => {
    try {
      const m = await api.resolveSession();
      setMe(m);
      persistMe(m);
    } catch {
      /* ignore */
    }
  }, []);

  const loading = auth.isLoading || resolving || auth.activeNavigator != null;

  return (
    <Ctx.Provider value={{ me, oidc: true, loading, signIn, loginWithSSO, signOut, reloadMe }}>
      {children}
    </Ctx.Provider>
  );
}

/** Legacy password-free email identity (unchanged behaviour). */
function LegacyIdentityProvider({ children }: { children: ReactNode }) {
  // `me` seeds from storage; the X-Actor-Id header is already seeded from its own key
  // at http.ts module load, so requests are attributed before this component mounts.
  const [me, setMe] = useState<Member | null>(loadMe);

  const signIn = useCallback(async (email: string, name?: string) => {
    const m = await api.signIn(email, name);
    setActorId(m.id);
    persistMe(m);
    setMe(m);
    return m;
  }, []);
  const loginWithSSO = useCallback(() => {
    /* no-op in legacy mode */
  }, []);
  const signOut = useCallback(() => {
    setActorId(null);
    persistMe(null);
    setMe(null);
  }, []);
  const reloadMe = useCallback(async () => {
    /* legacy: no server session to refresh */
  }, []);

  return (
    <Ctx.Provider value={{ me, oidc: false, loading: false, signIn, loginWithSSO, signOut, reloadMe }}>
      {children}
    </Ctx.Provider>
  );
}
